import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, UserTier, UserStatus } from '@/lib/index';
import { useAuth } from './useAuth';

const EDGE_FN = 'user_mgmt_v5_2026_05_10';

interface CreateUserData {
  username: string;
  password: string;
  nickname?: string;
  description?: string;
  tier: UserTier;
  ticketPrice: number;
  fightPercentage: number;
  creditLimit: number;
  creditRefreshRate?: 'auto_daily' | 'auto_weekly' | 'manual';
}

interface UpdateUserData {
  balance?: number;
  status?: UserStatus;
  ticketPrice?: number;
  fightPercentage?: number;
  fightPctLimit?: number;
  fightPctMode?: 'single' | 'multiple';
  parentFightPct?: number | null;
  fightOnOff?: boolean;
  creditLimit?: number;
  password?: string;
  nickname?: string;
  description?: string;
  creditRefreshRate?: 'auto_daily' | 'auto_weekly' | 'manual';
  firstLogin?: boolean;
}

/**
 * Per-relationship fight setting: records what the PARENT has set
 * against a specific CHILD. Stored in the fight_settings table.
 */
export interface FightSetting {
  parentId: string;
  childId: string;
  fightOnOff: boolean;  // true = parent has fight enabled against this child
  fightPct: number;     // the % parent fights against this child
  updatedAt?: string;   // ISO timestamp of when this setting was last changed
}

/** Map a raw DB row to our User interface */
function mapDbUser(dbUser: Record<string, unknown>): User {
  const tier = dbUser.tier as UserTier;
  const fightPct = parseInt((dbUser.fight_percentage as string) || '0');
  const fightPctLimitFallback =
    tier === UserTier.PLAYER ? 0
    : tier === UserTier.AGENT ? fightPct
    : 100;
  return {
    id: dbUser.id as string,
    username: dbUser.username as string,
    nickname: (dbUser.nickname as string) || undefined,
    description: (dbUser.description as string) || undefined,
    tier,
    status: dbUser.status as UserStatus,
    balance: dbUser.balance !== null && dbUser.balance !== undefined ? parseFloat(dbUser.balance as string) : 0,
    creditLimit: dbUser.credit_limit !== null && dbUser.credit_limit !== undefined ? parseFloat(dbUser.credit_limit as string) : 0,
    ticketPrice: dbUser.ticket_price !== null && dbUser.ticket_price !== undefined
      ? parseFloat(dbUser.ticket_price as string)
      : 1.0,
    fightPercentage: fightPct,
    fightPctLimit: dbUser.fight_pct_limit !== null && dbUser.fight_pct_limit !== undefined
      ? parseInt(dbUser.fight_pct_limit as string)
      : fightPctLimitFallback,
    fightPctMode: (dbUser.fight_pct_mode as 'single' | 'multiple') || 'single',
    parentFightPct: dbUser.parent_fight_pct !== null && dbUser.parent_fight_pct !== undefined
      ? parseInt(dbUser.parent_fight_pct as string)
      : undefined,
    fightOnOff: dbUser.fight_on_off !== null && dbUser.fight_on_off !== undefined
      ? Boolean(dbUser.fight_on_off)
      : false,
    creditRefreshRate: (dbUser.credit_refresh_rate as 'auto_daily' | 'auto_weekly' | 'manual') || 'manual',
    lastCreditRefresh: dbUser.last_credit_refresh as string | null | undefined,
    firstLogin: dbUser.first_login !== undefined ? Boolean(dbUser.first_login) : true,
    parentId: dbUser.parent_id as string | undefined,
    createdAt: dbUser.created_at as string,
  };
}

/**
 * Custom hook for user management — all operations go through
 * user_mgmt_v5_2026_05_10 edge function with action passed in the body.
 */
export function useUserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * fightSettings: map of "parentId:childId" → FightSetting
   * Represents PARENT's fight on/off & fight% against each direct CHILD.
   * This is independent of the child's own fight settings against their downlines.
   */
  const [fightSettings, setFightSettings] = useState<Map<string, FightSetting>>(new Map());

  /**
   * Look up a parent→child fight setting (individual relationship).
   * Never pass childId === parentId here; use getBlanketFightSetting for the blanket sentinel.
   */
  const getFightSetting = useCallback((parentId: string, childId: string): FightSetting => {
    return fightSettings.get(`${parentId}:${childId}`) ?? {
      parentId,
      childId,
      fightOnOff: false,
      fightPct: 0,
    };
  }, [fightSettings]);

  /**
   * Look up the blanket on/off for a parent (sentinel row: parent_id = child_id = parentId).
   * This is the parent's own blanket preference, completely independent of individual child rows.
   */
  const getBlanketFightSetting = useCallback((parentId: string): FightSetting => {
    return fightSettings.get(`${parentId}:${parentId}`) ?? {
      parentId,
      childId: parentId,
      fightOnOff: false,
      fightPct: 0,
    };
  }, [fightSettings]);

  // ── Load all users ────────────────────────────────────────────────────────
  const loadUsers = useCallback(async (silent = false) => {
    if (!currentUser) return;

    try {
      if (!silent) setIsLoading(true);
      setError(null);

      const { data, error: fnError } = await supabase.functions.invoke(EDGE_FN, {
        body: { action: 'list' },
      });

      if (fnError) throw new Error(`Function error: ${fnError.message}`);
      if (!data?.success) throw new Error(data?.error || 'Failed to load users');

      const transformed: User[] = (data.users as Record<string, unknown>[]).map(mapDbUser);
      setUsers(transformed);

      // Build fightSettings map from returned fight_settings rows
      const settingsMap = new Map<string, FightSetting>();
      if (data.fightSettings && Array.isArray(data.fightSettings)) {
        for (const row of data.fightSettings as Array<Record<string, unknown>>) {
          const fs: FightSetting = {
            parentId: row.parent_id as string,
            childId: row.child_id as string,
            fightOnOff: Boolean(row.fight_on_off),
            fightPct: parseInt((row.fight_pct as string) || '0'),
            updatedAt: row.updated_at as string | undefined,
          };
          settingsMap.set(`${fs.parentId}:${fs.childId}`, fs);
        }
      }
      setFightSettings(settingsMap);

    } catch (err) {
      console.error('[useUserManagement] loadUsers failed:', err);
      if (!silent) setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [currentUser]);

  /**
   * Set the PARENT's fight on/off (and optionally fight%) against a specific CHILD.
   * This does NOT affect the child's own fight settings against their downlines.
   */
  const setFightSetting = useCallback(async (
    parentId: string,
    childId: string,
    fightOnOff: boolean,
    fightPct?: number
  ): Promise<void> => {
    // Optimistic update
    const key = `${parentId}:${childId}`;
    setFightSettings(prev => {
      const next = new Map(prev);
      const existing = prev.get(key) ?? { parentId, childId, fightOnOff: false, fightPct: 0 };
      next.set(key, {
        ...existing,
        fightOnOff,
        // Always store the explicit pct if provided; only zero-out when turning off WITHOUT an explicit pct
        fightPct: fightPct !== undefined ? fightPct : (fightOnOff ? existing.fightPct : 0),
      });
      return next;
    });

    const { data, error: fnError } = await supabase.functions.invoke(EDGE_FN, {
      body: {
        action: 'set_fight_setting',
        parentId,
        childId,
        fightOnOff,
        ...(fightPct !== undefined ? { fightPct } : {}),
      },
    });

    if (fnError || !data?.success) {
      // Revert optimistic update on failure
      await loadUsers(true);
      throw new Error(fnError?.message || data?.error || 'Failed to update fight setting');
    }
  }, [loadUsers]);

  /**
   * Set the parent's BLANKET fight on/off (sentinel row: parent_id = child_id = parentId).
   * - ON:  sentinel ON + 0%, all children ON + 0% (user sets % manually after)
   * - OFF: sentinel OFF + 0%, all children OFF + 0%
   * Individual child toggles never call this — they use setFightSetting instead.
   */
  /**
   * Set the parent's BLANKET fight on/off (sentinel row: parent_id = child_id = parentId).
   * blanketPct: when turning ON with a specific %, pass it here (default 0).
   * The edge function handles per-child deduction (child's fight_pct_limit subtracted, min 0%).
   */
  const setFightBlanket = useCallback(async (parentId: string, fightOnOff: boolean, blanketPct = 0): Promise<void> => {
    // Optimistic update: sentinel always resets to blanketPct (or 0 if OFF)
    const sentinelKey = `${parentId}:${parentId}`;
    const effectivePct = fightOnOff ? blanketPct : 0;
    setFightSettings(prev => {
      const next = new Map(prev);
      const existing = prev.get(sentinelKey) ?? { parentId, childId: parentId, fightOnOff: false, fightPct: 0 };
      next.set(sentinelKey, { ...existing, fightOnOff, fightPct: effectivePct });
      // All direct children: switch ON/OFF, 0% (server will compute actual deducted pct)
      for (const [key, fs] of next.entries()) {
        if (fs.parentId === parentId && fs.childId !== parentId) {
          next.set(key, { ...fs, fightOnOff, fightPct: 0 });
        }
      }
      return next;
    });

    const { data, error: fnError } = await supabase.functions.invoke(EDGE_FN, {
      body: { action: 'set_fight_blanket', parentId, fightOnOff, blanketPct: effectivePct },
    });

    if (fnError || !data?.success) {
      await loadUsers(true);
      throw new Error(fnError?.message || data?.error || 'Failed to update blanket fight setting');
    }
    // Reload to get exact server-computed per-child values after deduction
    await loadUsers(true);
  }, [loadUsers]);

  // ── Create user ───────────────────────────────────────────────────────────
  const createUser = useCallback(async (userData: CreateUserData): Promise<User> => {
    if (!currentUser) throw new Error('Not authenticated');

    try {
      setIsLoading(true);
      setError(null);

      const finalCreditLimit = userData.tier === 'COMPANY' ? 50000000 : userData.creditLimit;
      const parentId = userData.tier === 'COMPANY' ? null : currentUser.id;

      const { data, error: fnError } = await supabase.functions.invoke(EDGE_FN, {
        body: {
          action: 'create',
          username: userData.username,
          password: userData.password,
          nickname: userData.nickname || null,
          description: userData.description || null,
          tier: userData.tier,
          parentId,
          creditLimit: finalCreditLimit,
          ticketPrice: userData.ticketPrice,
          fightPercentage: userData.fightPercentage,
          creditRefreshRate: userData.creditRefreshRate || 'manual',
        },
      });

      if (fnError) throw new Error(`Function error: ${fnError.message}`);
      if (!data?.success) throw new Error(data?.error || 'Failed to create user');

      const newUser = mapDbUser(data.user as Record<string, unknown>);
      setUsers(prev => [newUser, ...prev]);
      loadUsers(true);

      return newUser;

    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create user';
      setError(msg);
      throw new Error(msg);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, loadUsers]);

  // ── Update user ───────────────────────────────────────────────────────────
  const updateUser = useCallback(async (userId: string, updates: UpdateUserData): Promise<User> => {
    try {
      setError(null);

      const { data, error: fnError } = await supabase.functions.invoke(EDGE_FN, {
        body: { action: 'update', userId, updates },
      });

      if (fnError) throw new Error(`Function error: ${fnError.message}`);
      if (!data?.success) throw new Error(data?.error || 'Failed to update user');

      const updatedUser = mapDbUser(data.user as Record<string, unknown>);
      setUsers(prev => prev.map(u => u.id === userId ? updatedUser : u));
      return updatedUser;

    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update user';
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  // ── Change status (uses dedicated 'status' action so failed_login_attempts is reset on ACTIVE) ──
  const changeStatus = useCallback(async (userId: string, status: UserStatus): Promise<User> => {
    try {
      setError(null);

      const { data, error: fnError } = await supabase.functions.invoke(EDGE_FN, {
        body: { action: 'status', userId, status },
      });

      if (fnError) throw new Error(`Function error: ${fnError.message}`);
      if (!data?.success) throw new Error(data?.error || 'Failed to update status');

      const updatedUser = mapDbUser(data.user as Record<string, unknown>);
      // Update local state immediately
      setUsers(prev => prev.map(u => u.id === userId ? updatedUser : u));
      // Also silently reload to catch any DB-side changes (e.g. auto-bans from auth)
      loadUsers(true);
      return updatedUser;

    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update status';
      setError(msg);
      throw new Error(msg);
    }
  }, [loadUsers]);

  // ── Delete user ───────────────────────────────────────────────────────────
  const deleteUser = useCallback(async (userId: string): Promise<void> => {
    if (!currentUser) throw new Error('Not authenticated');
    if (currentUser.tier !== 'COMPANY') throw new Error('Only company users can delete accounts');

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fnError } = await supabase.functions.invoke(EDGE_FN, {
        body: { action: 'delete', userId },
      });

      if (fnError) throw new Error(`Function error: ${fnError.message}`);
      if (!data?.success) throw new Error(data?.error || 'Failed to delete user');

      setUsers(prev => prev.filter(u => u.id !== userId));

    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete user';
      setError(msg);
      throw new Error(msg);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (currentUser) loadUsers();
  }, [currentUser, loadUsers]);

  return {
    users,
    isLoading,
    error,
    loadUsers,
    createUser,
    updateUser,
    changeStatus,
    deleteUser,
    fightSettings,
    getFightSetting,
    getBlanketFightSetting,
    setFightSetting,
    setFightBlanket,
  };
}